// LoyaltyProgrammePage.duck.js

import { fetchCurrentUser } from '../../ducks/user.duck';
import { completeTask, TASK_NAMES } from '../../ducks/voucherify.duck';
import {
  getVoucherifyRewards,
  getVoucherifyTasks,
  joinVoucherifyLoyaltyProgram,
  redeemVoucherifyReward,
} from '../../util/api';
import { storableError } from '../../util/errors';
import { logError } from '../../util/log';
import { checkForProfileCompletion } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

// ================ Action types ================ //

export const FETCH_REWARDS_REQUEST = 'app/Points/FETCH_REWARDS_REQUEST';
export const FETCH_REWARDS_SUCCESS = 'app/Points/FETCH_REWARDS_SUCCESS';
export const FETCH_REWARDS_ERROR = 'app/Points/FETCH_REWARDS_ERROR';

export const FETCH_TASKS_REQUEST = 'app/Points/FETCH_TASKS_REQUEST';
export const FETCH_TASKS_SUCCESS = 'app/Points/FETCH_TASKS_SUCCESS';
export const FETCH_TASKS_ERROR = 'app/Points/FETCH_TASKS_ERROR';

export const UPDATE_LOYALTY_REQUEST = 'app/Points/UPDATE_LOYALTY_REQUEST';
export const UPDATE_LOYALTY_SUCCESS = 'app/Points/UPDATE_LOYALTY_SUCCESS';
export const UPDATE_LOYALTY_ERROR = 'app/Points/UPDATE_LOYALTY_ERROR';

export const REDEEM_REWARD_REQUEST = 'app/Points/REDEEM_REWARD_REQUEST';
export const REDEEM_REWARD_SUCCESS = 'app/Points/REDEEM_REWARD_SUCCESS';
export const REDEEM_REWARD_ERROR = 'app/Points/REDEEM_REWARD_ERROR';

export const JOIN_LOYALTY_REQUEST = 'app/Points/JOIN_LOYALTY_REQUEST';
export const JOIN_LOYALTY_SUCCESS = 'app/Points/JOIN_LOYALTY_SUCCESS';
export const JOIN_LOYALTY_ERROR = 'app/Points/JOIN_LOYALTY_ERROR';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.LoyaltyProgrammePage} */
const initialState = {
  memberRewards: [],
  memberRewardsInProgress: false,
  memberRewardsError: null,
  tasks: [],
  tasksInProgress: false,
  tasksError: null,
  redeemInProgress: false,
  redeemError: null,
  joinInProgress: false,
  joinError: null,
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case FETCH_REWARDS_REQUEST:
      return { ...state, memberRewardsInProgress: true, memberRewardsError: null };
    case FETCH_REWARDS_SUCCESS:
      return { ...state, memberRewardsInProgress: false, memberRewards: action.payload };
    case FETCH_REWARDS_ERROR:
      return { ...state, memberRewardsInProgress: false, memberRewardsError: action.payload };

    case FETCH_TASKS_REQUEST:
      return { ...state, tasksInProgress: true, tasksError: null };
    case FETCH_TASKS_SUCCESS:
      return { ...state, tasksInProgress: false, tasks: action.payload };
    case FETCH_TASKS_ERROR:
      return { ...state, tasksInProgress: false, tasksError: action.payload };

    case UPDATE_LOYALTY_REQUEST:
      return { ...state, loyaltyInProgress: true, loyaltyError: null };
    case UPDATE_LOYALTY_SUCCESS:
      return { ...state, loyaltyInProgress: false };
    case UPDATE_LOYALTY_ERROR:
      return { ...state, loyaltyInProgress: false, loyaltyError: action.payload };

    case REDEEM_REWARD_REQUEST:
      return { ...state, redeemInProgress: true, redeemError: null };
    case REDEEM_REWARD_SUCCESS:
      return { ...state, redeemInProgress: false };
    case REDEEM_REWARD_ERROR:
      return { ...state, redeemInProgress: false, redeemError: action.payload };

    case JOIN_LOYALTY_REQUEST:
      return { ...state, joinInProgress: true, joinError: null };
    case JOIN_LOYALTY_SUCCESS:
      return { ...state, joinInProgress: false };
    case JOIN_LOYALTY_ERROR:
      return { ...state, joinInProgress: false, joinError: action.payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const updateLoyaltyRequest = () => ({ type: UPDATE_LOYALTY_REQUEST });
const updateLoyaltySuccess = (response) => ({
  type: UPDATE_LOYALTY_SUCCESS,
  payload: response,
});
const updateLoyaltyError = (error) => ({
  type: UPDATE_LOYALTY_ERROR,
  error: true,
  payload: error,
});

const redeemRewardRequest = () => ({ type: REDEEM_REWARD_REQUEST });
const redeemRewardSuccess = (response) => ({
  type: REDEEM_REWARD_SUCCESS,
  payload: response,
});
const redeemRewardError = (error) => ({
  type: REDEEM_REWARD_ERROR,
  error: true,
  payload: error,
});

const joinLoyaltyRequest = () => ({ type: JOIN_LOYALTY_REQUEST });
const joinLoyaltySuccess = (response) => ({
  type: JOIN_LOYALTY_SUCCESS,
  payload: response,
});
const joinLoyaltyError = (error) => ({
  type: JOIN_LOYALTY_ERROR,
  error: true,
  payload: error,
});

const getMemberRewardsRequest = () => ({ type: FETCH_REWARDS_REQUEST });
const getMemberRewardsSuccess = (response) => ({
  type: FETCH_REWARDS_SUCCESS,
  payload: response,
});
const getMemberRewardsError = (error) => ({
  type: FETCH_REWARDS_ERROR,
  error: true,
  payload: error,
});

const getMemberTasksRequest = () => ({ type: FETCH_TASKS_REQUEST });
const getMemberTasksSuccess = (response) => ({
  type: FETCH_TASKS_SUCCESS,
  payload: response,
});
const getMemberTasksError = (error) => ({
  type: FETCH_TASKS_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //

/**
 * Complete a task and reward points by updating the unified loyalty JSON.
 *
 * @param {string} taskKey - The key of the task to complete (e.g. 'updatePhone').
 * @param {number} rewardPoints - The points to add when the task is completed.
 */
export const completeTaskAndReward = (taskKey, rewardPoints) => async (dispatch, getState, sdk) => {
  const currentUser = getState().user.currentUser;
  if (!currentUser) return;

  dispatch(updateLoyaltyRequest());
  try {
    dispatch(updateLoyaltySuccess());
  } catch (error) {
    logError(error, 'update-loyalty-failed');
    dispatch(updateLoyaltyError(storableError(error)));
  }
};

/**
 * Redeem a reward voucher via Voucherify API.
 * Uses the default campaign "10%_discount" unless reward.campaign is set.
 * @type {(rewardId: string, memberCode: string) => import('../../../types/vansite').AppThunkAction<Promise<import('@voucherify/sdk').LoyaltiesRedeemRewardResponse | undefined>>}
 */
export const redeemRewardVoucher = (rewardId, memberCode) => async (dispatch, getState) => {
  const currentUser = getState().user.currentUser;
  if (!currentUser) return;

  try {
    dispatch(redeemRewardRequest());
    /** @type {{data: import('@voucherify/sdk').LoyaltiesRedeemRewardResponse}} response - The response from the Voucherify API. */
    const response = await redeemVoucherifyReward({
      rewardId,
      memberCode,
    });
    dispatch(redeemRewardSuccess(response.data));
    return response.data;
  } catch (error) {
    logError(error, 'redeem-reward-voucher-failed');
    dispatch(redeemRewardError(storableError(error)));
  }
};

/**
 * Join the loyalty program by creating a default loyalty JSON for the user.
 * @type {() => import('../../../types/vansite').AppThunkAction<Promise<void>>}
 */
export const joinLoyaltyProgram = () => async (dispatch, getState, sdk) => {
  const currentUser = getState().user.currentUser;
  if (!currentUser) return;

  // If loyalty data already exists, no need to join again.
  const memberCode = currentUser.attributes.profile.privateData.loyalty?.memberCode;
  if (memberCode) {
    return;
  }

  const voucherifyCustomerId = currentUser.attributes.profile.privateData.voucherifyCustomer?.id;
  if (!voucherifyCustomerId) {
    return;
  }

  try {
    dispatch(joinLoyaltyRequest());

    /** @type {{data: {loyaltyCode: string, referralCode: string}}} response - The response from the Voucherify API. */
    await joinVoucherifyLoyaltyProgram({ customerId: voucherifyCustomerId });
    await dispatch(fetchCurrentUser());
    dispatch(joinLoyaltySuccess());

    dispatch(fetchRewards());
    dispatch(fetchMemberTasks());

    const currentUser = getState().user.currentUser;
    if (checkForProfileCompletion(currentUser)) {
      dispatch(completeTask(TASK_NAMES.PROFILE_FILLED_OUT));
    }
  } catch (error) {
    logError(error, 'join-loyalty-failed');
    dispatch(joinLoyaltyError(storableError(error)));
  }
};

/**
 * Get the tasks for the current user.
 * @type {() => import('../../../types/vansite').AppThunkAction<Promise<void>>}
 */
export const fetchMemberTasks = () => async (dispatch, getState) => {
  const currentUser = getState().user.currentUser;
  if (!currentUser) return;

  try {
    dispatch(getMemberTasksRequest());
    const response = await getVoucherifyTasks({});
    dispatch(getMemberTasksSuccess(response.data));
  } catch (error) {
    logError(error, 'get-member-tasks-failed');
    dispatch(getMemberTasksError(storableError(error)));
  }
};

/**
 * Get the rewards for the current user.
 * @type {() => import('../../../types/vansite').AppThunkAction<Promise<void>>}
 */
export const fetchRewards = () => async (dispatch, getState) => {
  const currentUser = getState().user.currentUser;
  if (!currentUser) return;

  const memberCode = currentUser.attributes.profile.privateData.loyalty?.memberCode;
  if (!memberCode) {
    dispatch(getMemberRewardsSuccess([]));
    return;
  }

  try {
    dispatch(getMemberRewardsRequest());
    const response = await getVoucherifyRewards({ memberCode });
    dispatch(
      getMemberRewardsSuccess(
        response.data.sort(
          (a, b) => a.assignment.parameters.loyalty.points - b.assignment.parameters.loyalty.points,
        ),
      ),
    );
  } catch (error) {
    logError(error, 'get-member-rewards-failed');
    dispatch(getMemberRewardsError(storableError(error)));
  }
};

/**
 * Load the loyalty data for the current user.
 */
export const loadData = () => async (dispatch, getState, sdk) => {
  await dispatch(fetchCurrentUser());
  return await Promise.all([
    dispatch(fetchRewards()),
    dispatch(fetchMemberTasks()),
  ]);
};
